import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCallback } from "react";

export interface FinancialRiskData {
  currentNfci: number | null;
  weeklyChange: number | null;
  invertedChange: number | null;
  regime: 'green' | 'red' | 'neutral';
  flip: boolean;
  flipDirection: 'up' | 'down' | null;
  lastFlip: { date: string; direction: 'up' | 'down' } | null;
  history: Array<{
    date: string;
    value: number | null;
    inverted: number | null;
    regime: string;
  }>;
}

// Removed 'orange' (recession risk) - now only green/red/neutral
export type CSAlphaRegime = 'green' | 'red' | 'neutral';

export interface CreditSpreadAlphaData {
  current: number | null;
  sma: number | null;
  upperThreshold: number | null;
  lowerThreshold: number;
  us10y: number | null;
  us2y: number | null;
  hyYield: number | null;
  yieldCurveInverted: boolean;
  regime: CSAlphaRegime;
  lastCrossing: { date: string; type: 'widening' | 'compressed'; regime: CSAlphaRegime } | null;
  config: {
    smaLength: number;
    stdMult: number;
    lowThreshold: number;
  };
  history: Array<{
    date: string;
    value: number;
    sma: number | null;
    upperThreshold: number | null;
    regime: CSAlphaRegime;
    yieldCurveInverted: boolean;
    wideningCross: boolean;
    compressedCross: boolean;
  }>;
}

export interface SeriesData {
  current: number | null;
  change7d?: number | null;
  change7dPct?: number | null;
  change30d?: number | null;
  change30dPct?: number | null;
  changeYoY?: number | null;
  changeYoYPct?: number | null;
  history: Array<{ date: string; value: number | null }>;
}

export interface MacroData {
  financialRisk: FinancialRiskData;
  creditSpreadAlpha: CreditSpreadAlphaData | null;
  m2: SeriesData;
  rrp: SeriesData;
  fedBalance: SeriesData;
  netLiquidity: SeriesData;
  fedFunds: SeriesData;
  treasury10y: SeriesData;
  treasury2y: SeriesData;
  yieldSpread: number | null;
  stlFsi: SeriesData;    // St. Louis Fed Financial Stress Index
  iorb: SeriesData;      // Interest on Reserve Balances
  tga?: SeriesData;      // Treasury General Account (WTREGEN)
  sofr?: SeriesData;     // Secured Overnight Financing Rate
  dtb3?: SeriesData;     // 3-Month Treasury Bill Rate
  sofrTbillSpread?: SeriesData; // SOFR - DTB3 spread
  lastUpdated: string;
}

async function fetchMacroData(forceRefresh = false): Promise<MacroData> {
  const options: { headers?: Record<string, string> } = {};
  
  if (forceRefresh) {
    options.headers = { 'x-force-refresh': '1' };
  }
  
  const { data, error } = await supabase.functions.invoke('fred-macro-data', options);
  
  if (error) {
    console.error('Error fetching macro data:', error);
    throw new Error(error.message || 'Failed to fetch macro data');
  }
  
  console.log(`Macro data loaded: ${data?.financialRisk?.history?.length ?? 0} NFCI points, ${data?.creditSpreadAlpha?.history?.length ?? 0} CS Alpha points, SOFR: ${data?.sofr?.current ?? 'N/A'}`);
  
  return data as MacroData;
}

export function useMacroData() {
  const queryClient = useQueryClient();
  
  const query = useQuery({
    queryKey: ['macro-data'],
    queryFn: () => fetchMacroData(false),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    retry: 2,
    refetchOnWindowFocus: false,
  });

  // Force refresh function that bypasses cache
  const forceRefresh = useCallback(async () => {
    // Invalidate cache first to ensure fresh fetch
    await queryClient.invalidateQueries({ queryKey: ['macro-data'] });
    const freshData = await fetchMacroData(true);
    queryClient.setQueryData(['macro-data'], freshData);
    return freshData;
  }, [queryClient]);

  return {
    ...query,
    forceRefresh,
  };
}
